import {
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Ref,
} from 'vue'
import {
  FAIR_HOME_STICKY_COLLAPSE_LOCK_MS,
  FAIR_HOME_STICKY_PIN_GRACE_MS,
  FAIR_HOME_STICKY_SCROLL_SETTLE_MS,
  canCommitFairHomeStickyCollapse,
  canFairHomeStickyChrome,
  cancelFairHomeStickyIdleCommit,
  resolveFairHomeStickyMegaCollapsed,
  scheduleFairHomeStickyIdleCommit,
  shouldFairHomeStickyPin,
} from './fair-home-sticky.data'

/**
 * Page-level sticky chrome: pin dark header + mega when chips leave,
 * collapse expandable row on scroll-down (with grace after handoff).
 *
 * Scroll-driven commits wait for scroll silence + requestIdleCallback so
 * max-height dissolve does not run mid-wheel on a busy main thread.
 */
export function useFairHomeStickyChrome(
  menuEl: Ref<HTMLElement | null | undefined>,
) {
  const viewportWidth = ref(
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  )
  const isPinned = ref(false)
  const searchOpen = ref(false)
  const megaCollapsed = ref(false)
  /** Sticky header mega dropdown open — blocks scroll-down collapse. */
  const megaPanelOpen = ref(false)
  const lastScrollY = ref(
    typeof window !== 'undefined' ? window.scrollY : 0,
  )
  const pinGraceUntilMs = ref(0)

  /** Latest scroll-direction intent; applied when unlock allows. */
  let pendingCollapsed = false
  let lockUntilMs = 0
  let unlockTimer = 0
  let idleCommitId = 0
  let settleTimer = 0
  let raf = 0
  /** Signed scroll sum since last commit — softens per-frame wheel noise. */
  let directionAccum = 0

  const enabled = () => canFairHomeStickyChrome(viewportWidth.value)

  function now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now()
  }

  function clearUnlockTimer() {
    if (!unlockTimer) return
    window.clearTimeout(unlockTimer)
    unlockTimer = 0
  }

  function clearIdleCommit() {
    cancelFairHomeStickyIdleCommit(idleCommitId)
    idleCommitId = 0
    if (!settleTimer) return
    window.clearTimeout(settleTimer)
    settleTimer = 0
  }

  function syncScrollBaseline() {
    if (typeof window === 'undefined') return
    lastScrollY.value = window.scrollY
  }

  function commitCollapsed(next: boolean, atMs: number) {
    if (!isPinned.value) {
      megaCollapsed.value = false
      pendingCollapsed = false
      return
    }
    if (megaCollapsed.value === next) return

    megaCollapsed.value = next
    pendingCollapsed = next
    directionAccum = 0
    lockUntilMs = atMs + FAIR_HOME_STICKY_COLLAPSE_LOCK_MS
    clearUnlockTimer()
    unlockTimer = window.setTimeout(() => {
      unlockTimer = 0
      syncScrollBaseline()
      pendingCollapsed = megaCollapsed.value
      directionAccum = 0
    }, FAIR_HOME_STICKY_COLLAPSE_LOCK_MS)
  }

  function commitCollapsedImmediate(next: boolean) {
    clearIdleCommit()
    commitCollapsed(next, now())
  }

  function scheduleCommitCollapsed(next: boolean) {
    pendingCollapsed = next
    clearIdleCommit()
    settleTimer = window.setTimeout(() => {
      settleTimer = 0
      idleCommitId = scheduleFairHomeStickyIdleCommit(() => {
        idleCommitId = 0
        if (megaCollapsed.value === pendingCollapsed) return
        commitCollapsed(pendingCollapsed, now())
      }, 0)
    }, FAIR_HOME_STICKY_SCROLL_SETTLE_MS)
  }

  function resetCollapseState() {
    clearUnlockTimer()
    clearIdleCommit()
    pendingCollapsed = false
    megaCollapsed.value = false
    lockUntilMs = 0
    pinGraceUntilMs.value = 0
    directionAccum = 0
  }

  function setMegaPanelOpen(open: boolean) {
    megaPanelOpen.value = open
    if (!open || !isPinned.value) return
    pendingCollapsed = false
    lockUntilMs = 0
    clearUnlockTimer()
    commitCollapsedImmediate(false)
  }

  function measure() {
    const el = menuEl.value
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0
    const scrollDelta = scrollY - lastScrollY.value
    const atMs = now()

    if (!el) {
      isPinned.value = false
      searchOpen.value = false
      megaPanelOpen.value = false
      resetCollapseState()
      lastScrollY.value = scrollY
      return
    }

    const top = el.getBoundingClientRect().top
    const nextPinned = shouldFairHomeStickyPin(top, enabled(), isPinned.value)

    if (!nextPinned) {
      searchOpen.value = false
      megaPanelOpen.value = false
      resetCollapseState()
    } else if (!isPinned.value) {
      pendingCollapsed = false
      megaCollapsed.value = false
      lockUntilMs = 0
      clearUnlockTimer()
      clearIdleCommit()
      pinGraceUntilMs.value = atMs + FAIR_HOME_STICKY_PIN_GRACE_MS
    } else if (atMs >= lockUntilMs) {
      if (
        directionAccum !== 0 &&
        Math.sign(scrollDelta) !== Math.sign(directionAccum)
      ) {
        directionAccum = scrollDelta
      } else {
        directionAccum += scrollDelta
      }

      pendingCollapsed = resolveFairHomeStickyMegaCollapsed({
        isPinned: true,
        scrollDelta: directionAccum,
        currentCollapsed: megaCollapsed.value,
        nowMs: atMs,
        pinGraceUntilMs: pinGraceUntilMs.value,
        megaPanelOpen: megaPanelOpen.value,
      })

      if (
        canCommitFairHomeStickyCollapse({
          pendingCollapsed,
          currentCollapsed: megaCollapsed.value,
          nowMs: atMs,
          lockUntilMs,
        })
      ) {
        scheduleCommitCollapsed(pendingCollapsed)
      } else if (idleCommitId || settleTimer) {
        if (pendingCollapsed === megaCollapsed.value) {
          clearIdleCommit()
        }
      }
    }

    isPinned.value = nextPinned
    lastScrollY.value = scrollY
  }

  function handleResize() {
    viewportWidth.value = window.innerWidth
    measure()
  }

  function handleScroll() {
    if (raf) return
    raf = window.requestAnimationFrame(() => {
      raf = 0
      measure()
    })
  }

  onMounted(() => {
    lastScrollY.value = window.scrollY
    handleResize()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
    clearUnlockTimer()
    clearIdleCommit()
    if (raf) window.cancelAnimationFrame(raf)
  })

  watch(menuEl, () => measure())

  watch(searchOpen, (open) => {
    if (!open || !isPinned.value) return
    pendingCollapsed = false
    lockUntilMs = 0
    clearUnlockTimer()
    commitCollapsedImmediate(false)
  })

  return {
    isPinned,
    searchOpen,
    megaCollapsed,
    megaPanelOpen,
    setMegaPanelOpen,
    enabled,
    measure,
  }
}
